import { craneActions } from "../data/actions";
import { alertDefinitions } from "../data/alerts";
import { Alert, ProcedureDefinition } from "../types";
import { CraneActionKey } from "../data/keys";
import { AlertDefinitionKey } from "../data/keys";

// Helper to get alert type from definition
const getAlertType = (alert: Alert) => {
  const definition =
    alertDefinitions[alert.definitionId as keyof typeof alertDefinitions];
  return definition?.type || "info";
};

// Helper to get alert priority from definition
const getAlertPriority = (alert: Alert) => {
  const definition =
    alertDefinitions[alert.definitionId as keyof typeof alertDefinitions];
  return definition?.priority || 0;
};

// Keeping relevance helper removed for now as it's unused; reintroduce when needed.

export const calculateStatusCounts = (
  alerts: Alert[],
  currentProcedure: string
) => {
  return alerts
    .filter((alert) => !alert.acknowledged)
    .reduce(
      (counts, alert) => {
        const alertType = getAlertType(alert);
        counts[alertType]++;
        return counts;
      },
      { error: 0, warning: 0, info: 0, critical: 0 } as Record<string, number>
    );
};

export const filterAlerts = (
  alerts: Alert[],
  filter: "all" | "error" | "warning" | "info" | "critical"
) => {
  if (filter === "all") return alerts;
  return alerts.filter((alert) => getAlertType(alert) === filter);
};

export const getBannerAlerts = (alerts: Alert[], limit = 4) => {
  return alerts
    .filter((alert) => !alert.acknowledged)
    .sort((a, b) => {
      // Sort by creation time first (newest first)
      const createdCompare = (b.createdAt || 0) - (a.createdAt || 0);
      if (createdCompare !== 0) return createdCompare;

      // Then by priority
      const priorityA = getAlertPriority(a);
      const priorityB = getAlertPriority(b);
      return priorityB - priorityA;
    })
    .slice(0, limit);
};

// Returns true if an alert should display its type-based color accent
// until 3 minutes have passed or until user has interacted.
export const isAlertFresh = (
  alert: Alert,
  nowMs: number = Date.now()
): boolean => {
  if (alert.interacted) return false;
  const createdAt = alert.createdAt ?? nowMs;
  const threeMinutesMs = 3 * 60 * 1000;
  // const threeMinutesMs = 30 * 1000;
  return nowMs - createdAt < threeMinutesMs;
};

// Compute which actions are blocked/possible for a given procedure based on current alerts
export const getActionAvailability = (
  alerts: Alert[],
  procedure: ProcedureDefinition | undefined
): { notPossible: CraneActionKey[]; possible: CraneActionKey[] } => {
  if (!procedure) {
    return { notPossible: [], possible: [] };
  }

  const blocked = new Set<CraneActionKey>();

  for (const actionKey of procedure.actions) {
    const action = craneActions[actionKey];
    if (!action) continue;

    const hasBlockingWarning = alerts.some((a) => {
      const isRelevant = action.relevantAlertDefinitions.includes(
        a.definitionId
      );
      if (!isRelevant) return false;
      return true;
    });

    if (hasBlockingWarning) {
      blocked.add(actionKey);
    }
  }

  const notPossible = Array.from(blocked);
  const possible = procedure.actions.filter((k) => !blocked.has(k));
  return { notPossible, possible };
};

// Central selector for notifications panel
export const selectPanelAlerts = (
  alerts: Alert[],
  filter: "all" | "error" | "warning" | "info" | "critical"
): Alert[] => {
  const filtered = filterAlerts(alerts, filter);
  return filtered.slice().sort((a, b) => {
    if (a.acknowledged !== b.acknowledged) {
      return a.acknowledged ? 1 : -1;
    }
    // newest first by createdAt fallback to timestamp string compare
    const createdCompare = (b.createdAt || 0) - (a.createdAt || 0);
    if (createdCompare !== 0) return createdCompare;
    return b.timestamp.localeCompare(a.timestamp);
  });
};

// Count errors and warnings for a given set of related alert definition keys
export const countAlertsForDefinitions = (
  alerts: Alert[],
  relatedDefinitions: AlertDefinitionKey[]
): { error: number; warning: number } => {
  let error = 0;
  let warning = 0;
  for (const alert of alerts) {
    if (alert.acknowledged) continue;
    if (!relatedDefinitions.includes(alert.definitionId)) continue;
    const type = getAlertType(alert);
    if (type === "error" || type === "critical") {
      error += 1;
    } else if (type === "warning") {
      warning += 1;
    }
  }
  return { error, warning };
};
